import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common';
import SupplyOrderStatusChip from './SupplyOrderStatusChip';
import SupplyOrderActionDialog from './SupplyOrderActionDialog';
import ReceiveSupplyOrderDialog from './ReceiveSupplyOrderDialog';
import {
  canAcknowledgeSupplyOrder,
  canCancelSupplyOrder,
  canCloseSupplyOrder,
  canEditSupplyOrder,
  canIssueSupplyOrder,
  canMarkSupplyOrderSent,
  canPrintSupplyOrder,
  canReceiveSupplyOrder,
  formatSupplyOrderDate,
  formatSupplyOrderQuantity,
  formatSourceType,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';

const SupplyOrderDetailDialog = ({
  open,
  onClose,
  order,
  isLoading = false,
  onEdit,
  onIssue,
  onMarkSent,
  onAcknowledge,
  onReceive,
  onCancel,
  onCloseOrder,
  onPrint,
  isProcessing = false,
}) => {
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setResultMessage('');
      setErrorMessage('');
      setActionDialog(null);
      setReceiveDialogOpen(false);
    }
  }, [open, order?.uuid]);

  const receipts = useMemo(() => order?.receipts || [], [order?.receipts]);

  const runAction = async (actionFn, successMessage) => {
    try {
      setErrorMessage('');
      await actionFn();
      setResultMessage(successMessage);
    } catch (error) {
      setErrorMessage(getSupplyOrderApiErrorMessage(error));
    }
  };

  const handleIssue = () =>
    runAction(() => onIssue(order.uuid), 'Orden emitida correctamente.');

  const handleAcknowledge = () =>
    runAction(() => onAcknowledge(order.uuid), 'Orden confirmada correctamente.');

  const handlePrint = async () => {
    try {
      setErrorMessage('');
      await onPrint(order.uuid);
      setResultMessage('PDF generado correctamente.');
    } catch (error) {
      setErrorMessage(getSupplyOrderApiErrorMessage(error, 'No se pudo generar el PDF'));
    }
  };

  if (!open) return null;

  if (isLoading || !order) {
    return (
      <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
        <DialogTitle>Detalle de la orden</DialogTitle>
        <DialogBody>
          <TableBodySkeleton />
        </DialogBody>
        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>
    );
  }

  return (
    <>
      <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
        <DialogTitle>
          Orden {order.order_code || 'borrador'}
        </DialogTitle>

        <DialogBody>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            {resultMessage && <Alert severity="info">{resultMessage}</Alert>}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <SupplyOrderStatusChip status={order.status} />
              <Typography variant="body2" color="text.secondary">
                Tipo: {formatSourceType(order.source_type)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Origen: {order.source_label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Destino: {order.destination_label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fecha: {formatSupplyOrderDate(order.order_date)}
              </Typography>
              {order.source_type === 'provider' && (
                <Typography variant="body2" color="text.secondary">
                  Compromiso proveedor: {formatSupplyOrderDate(order.provider_delivery_commitment_at)}
                </Typography>
              )}
              {order.external_order_label && order.external_order_label !== '-' && (
                <Typography variant="body2" color="text.secondary">
                  Pedido técnico: {order.external_order_label}
                </Typography>
              )}
            </Box>

            {(order.conditions || order.observations) && (
              <Box>
                {order.conditions && (
                  <Typography variant="body2" color="text.secondary">
                    Condiciones: {order.conditions}
                  </Typography>
                )}
                {order.observations && (
                  <Typography variant="body2" color="text.secondary">
                    Observaciones: {order.observations}
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="subtitle1" fontWeight={700}>
              Artículos
            </Typography>

            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Artículo</HeadTableCell>
                    <HeadTableCell>Solicitada</HeadTableCell>
                    <HeadTableCell>Recibida</HeadTableCell>
                    <HeadTableCell>Pendiente</HeadTableCell>
                    <HeadTableCell>Cancelada</HeadTableCell>
                    <HeadTableCell>Cerrada c/dif.</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).length === 0 ? (
                    <TableRow>
                      <RowTableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                        Sin artículos
                      </RowTableCell>
                    </TableRow>
                  ) : (
                    (order.items || []).map((item) => (
                      <TableRow key={item.uuid}>
                        <RowTableCell>
                          {item.article_code_snapshot} — {item.article_name_snapshot}
                        </RowTableCell>
                        <RowTableCell>
                          {formatSupplyOrderQuantity(item.requested_quantity)}
                        </RowTableCell>
                        <RowTableCell>
                          {formatSupplyOrderQuantity(item.received_quantity)}
                        </RowTableCell>
                        <RowTableCell>
                          {formatSupplyOrderQuantity(item.pending_quantity)}
                        </RowTableCell>
                        <RowTableCell>
                          {formatSupplyOrderQuantity(item.cancelled_quantity)}
                        </RowTableCell>
                        <RowTableCell>
                          {formatSupplyOrderQuantity(item.closed_difference_quantity)}
                        </RowTableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </GeneralTableBox>

            {receipts.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight={700}>
                  Historial de recepciones
                </Typography>
                <GeneralTableBox>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <HeadTableCell>Código</HeadTableCell>
                        <HeadTableCell>Fecha</HeadTableCell>
                        <HeadTableCell>Documento</HeadTableCell>
                        <HeadTableCell>Estado</HeadTableCell>
                        <HeadTableCell>Artículos recibidos</HeadTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receipts.map((receipt) => (
                        <TableRow key={receipt.uuid}>
                          <RowTableCell>{receipt.receipt_code}</RowTableCell>
                          <RowTableCell>
                            {formatSupplyOrderDate(receipt.received_at)}
                          </RowTableCell>
                          <RowTableCell>
                            {receipt.supplier_document_type || '-'}
                            {receipt.supplier_document_number
                              ? ` ${receipt.supplier_document_number}`
                              : ''}
                          </RowTableCell>
                          <RowTableCell>{receipt.status}</RowTableCell>
                          <RowTableCell>
                            {(receipt.items || [])
                              .map(
                                (item) =>
                                  `${formatSupplyOrderQuantity(item.received_quantity)} u.`
                              )
                              .join(', ') || '-'}
                          </RowTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </GeneralTableBox>
              </>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {canEditSupplyOrder(order) && onEdit && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => onEdit(order)}
                  disabled={isProcessing}
                >
                  Editar
                </Button>
              )}
              {canIssueSupplyOrder(order) && (
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleIssue}
                  disabled={isProcessing}
                >
                  Emitir
                </Button>
              )}
              {canPrintSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={isProcessing}
                >
                  Imprimir PDF
                </Button>
              )}
              {canMarkSupplyOrderSent(order) && (
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setActionDialog('mark-sent')}
                  disabled={isProcessing}
                >
                  Marcar enviada
                </Button>
              )}
              {canAcknowledgeSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleAcknowledge}
                  disabled={isProcessing}
                >
                  Confirmar
                </Button>
              )}
              {canReceiveSupplyOrder(order) && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => setReceiveDialogOpen(true)}
                  disabled={isProcessing}
                >
                  Recibir
                </Button>
              )}
              {canCloseSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<LockIcon />}
                  onClick={() => setActionDialog('close')}
                  disabled={isProcessing}
                >
                  Cerrar
                </Button>
              )}
              {canCancelSupplyOrder(order) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setActionDialog('cancel')}
                  disabled={isProcessing}
                >
                  Cancelar
                </Button>
              )}
            </Box>
          </Box>
        </DialogBody>

        <DialogActions
          handleClose={onClose}
          handleSave={() => {}}
          saveButtonProps={{ sx: { display: 'none' } }}
          cancelLabel="Cerrar"
        />
      </DialogContainer>

      <SupplyOrderActionDialog
        open={actionDialog === 'cancel'}
        onClose={() => setActionDialog(null)}
        title="Cancelar orden"
        description="Esta acción cancelará la orden de abastecimiento. Indicá el motivo."
        confirmLabel="Cancelar orden"
        requireReason
        isProcessing={isProcessing}
        onConfirm={async ({ reason }) => {
          await onCancel({ id: order.uuid, reason });
          setResultMessage('Orden cancelada correctamente.');
        }}
      />

      <SupplyOrderActionDialog
        open={actionDialog === 'close'}
        onClose={() => setActionDialog(null)}
        title="Cerrar orden"
        description="Se cerrará el saldo pendiente de la orden con diferencia."
        confirmLabel="Cerrar orden"
        requireReason
        isProcessing={isProcessing}
        onConfirm={async ({ reason }) => {
          await onCloseOrder({ id: order.uuid, reason });
          setResultMessage('Orden cerrada correctamente.');
        }}
      />

      <SupplyOrderActionDialog
        open={actionDialog === 'mark-sent'}
        onClose={() => setActionDialog(null)}
        title="Marcar como enviada"
        description="La orden pasará al estado enviada."
        confirmLabel="Marcar enviada"
        requireEmail
        isProcessing={isProcessing}
        onConfirm={async ({ recipient_email }) => {
          await onMarkSent({ id: order.uuid, recipient_email });
          setResultMessage('Orden marcada como enviada.');
        }}
      />

      <ReceiveSupplyOrderDialog
        open={receiveDialogOpen}
        onClose={() => setReceiveDialogOpen(false)}
        order={order}
        onReceive={(payload) => onReceive({ id: order.uuid, data: payload })}
        isProcessing={isProcessing}
      />
    </>
  );
};

export default SupplyOrderDetailDialog;
